import { API_CONFIG } from "./config";

const responseCache = new Map();
const inFlightRequests = new Map();

const fetchJson = async (url) => {
  if (responseCache.has(url)) {
    return responseCache.get(url);
  }

  if (!inFlightRequests.has(url)) {
    const request = fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Request failed with ${response.status}`);
        }
        return response.json();
      })
      .then((payload) => {
        responseCache.set(url, payload);
        return payload;
      })
      .finally(() => {
        inFlightRequests.delete(url);
      });

    inFlightRequests.set(url, request);
  }

  return inFlightRequests.get(url);
};

const postJson = async (url, body) => {
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      accept: "*/*",
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(`Request failed with ${response.status}`);
  }

  const payload = await response.json();
  if (payload?.status !== 1) {
    throw new Error(payload?.error?.message || "Request failed.");
  }

  return payload;
};

const getSevasUrl = () => {
  if (!API_CONFIG.sevasBaseUrl || !API_CONFIG.sevasInstitutionReference) {
    throw new Error("Sevas API configuration is missing.");
  }

  const params = new URLSearchParams({
    institutionReference: API_CONFIG.sevasInstitutionReference,
    sevaCategory: "1",
    index: "0",
    size: "1000",
    sort_by: "sevaTypesId",
    sort_order: "desc",
  });

  return `${API_CONFIG.sevasBaseUrl}/seva-types?${params.toString()}`;
};

const mapSeva = (seva) => {
  const amount = (seva.fundGroups || []).reduce((sum, fundGroup) => sum + (fundGroup.amount || 0), 0);

  return {
    id: seva.sevaTypesId,
    name: seva.name,
    displayName: seva.displayName,
    amount,
    description: seva.description || "",
    fundGroups: seva.fundGroups || [],
    channelSourceType: seva.channelSourceType,
  };
};

const mapOption = (item, idKey) => ({
  id: String(item[idKey]),
  name: item.name,
  nativeName: item.nativeName,
});

const mapPaymentChannel = (channel) => ({
  id: String(channel.paymentChannelId),
  name: channel.name,
  label: channel.description || channel.name,
  sourceType: channel.sourceType,
});

export const fetchSevas = async () => {
  const payload = await fetchJson(getSevasUrl());
  return (payload?.data?.results || []).map(mapSeva);
};

export const fetchZodiacSigns = async () => {
  const payload = await fetchJson(`${API_CONFIG.sevasBaseUrl}/zodiac-signs`);
  return (payload?.data || []).map((item) => mapOption(item, "zodiacSignId"));
};

export const fetchNakshatras = async () => {
  const payload = await fetchJson(`${API_CONFIG.sevasBaseUrl}/nakshatras`);
  return (payload?.data || []).map((item) => mapOption(item, "nakshatraId"));
};

export const fetchGothras = async () => {
  const payload = await fetchJson(`${API_CONFIG.sevasBaseUrl}/gothras`);
  return (payload?.data || []).map((item) => mapOption(item, "gothraId"));
};

export const fetchPaymentChannels = async () => {
  const payload = await fetchJson(`${API_CONFIG.sevasBaseUrl}/payment-channels`);
  return (payload?.data || []).map(mapPaymentChannel);
};

export const fetchSevaBookingOptions = async () => {
  if (!API_CONFIG.sevasBaseUrl) {
    throw new Error("Sevas API configuration is missing.");
  }

  const [sevas, zodiacSigns, nakshatras, gothras, paymentChannels] = await Promise.all([
    fetchSevas(),
    fetchZodiacSigns(),
    fetchNakshatras(),
    fetchGothras(),
    fetchPaymentChannels(),
  ]);

  return { sevas, zodiacSigns, nakshatras, gothras, paymentChannels };
};

export const createSevaPayment = async (paymentPayload) => {
  if (!API_CONFIG.sevasBaseUrl) {
    throw new Error("Sevas API configuration is missing.");
  }

  const payload = await postJson(`${API_CONFIG.sevasBaseUrl}/create-payment`, paymentPayload);
  return payload?.data;
};

export const fetchPaymentStatus = async (orderId) => {
  if (!API_CONFIG.sevasBaseUrl) {
    throw new Error("Sevas API configuration is missing.");
  }

  const params = new URLSearchParams({ orderId });
  const payload = await fetchJson(`${API_CONFIG.sevasBaseUrl}/get-payment-status?${params.toString()}`);
  return payload?.data?.status || "pending";
};
